import React, { useEffect, useMemo, useRef, useState } from "react";
import { previewRateCard } from "../../services/apiService";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  formatMoney,
  formatNum,
  matchBand,
  relevantDimForBand,
} from "./rateCardConstants";

const ZERO_RATES = { cnc: 0, cut: 0, fab: 0, paint: 0 };

const DEFAULT_SCENARIOS = [
  { key: "small", label: "Small", width: "18", height: "36", length: "" },
  { key: "typical", label: "Typical", width: "30", height: "60", length: "" },
  { key: "large", label: "Large", width: "48", height: "96", length: "" },
];

const DEBOUNCE_MS = 350;

/**
 * Sticky right rail: three live-priced size scenarios that react to the current
 * form values plus a shared material + finish selector. Debounced, and cancels
 * stale requests so a slow response never overwrites a newer one.
 */
function RateCardPreviewRail({
  formValues,
  materials,
  finishRates,
  onPricing,
}) {
  const [scenarios, setScenarios] = useState(DEFAULT_SCENARIOS);
  const [materialId, setMaterialId] = useState("");
  const [finishRateId, setFinishRateId] = useState("");
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  // Held in a ref so a new callback identity never re-fires the preview.
  const onPricingRef = useRef(onPricing);
  onPricingRef.current = onPricing;

  const banded = !!formValues.banded;
  const bandRows = useMemo(() => formValues.bands || [], [formValues.bands]);
  const bandDim = formValues.band_dim;
  const rates = formValues.labor_rates || {};

  const hasLabor = banded
    ? bandRows.some((b) =>
        ["cnc", "cut", "fab", "paint"].some((d) => Number(b.rates?.[d]) > 0),
      )
    : ["cnc", "cut", "fab", "paint"].some((d) => Number(rates[d]) > 0);

  // Per-scenario: which band matches, and the rates to price it with.
  const matchedByKey = useMemo(() => {
    const map = {};
    scenarios.forEach((s) => {
      if (!banded) {
        map[s.key] = { band: null, rates };
        return;
      }
      const value = relevantDimForBand(bandDim, s);
      const band = matchBand(bandRows, value);
      map[s.key] = { band, rates: band ? band.rates : ZERO_RATES };
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, banded, bandDim, bandRows, JSON.stringify(rates)]);

  const setScenarioDim = (key, dim, value) => {
    setScenarios((prev) =>
      prev.map((s) => (s.key === key ? { ...s, [dim]: value } : s)),
    );
  };

  // Serialize the inputs that drive pricing so the effect only refires on change.
  const depKey = JSON.stringify({
    scenarios,
    materialId,
    finishRateId,
    geometry_method: formValues.geometry_method,
    labor_basis: formValues.labor_basis,
    banded,
    bandDim,
    bandRows,
    rates,
    min_labor_hours: formValues.min_labor_hours,
  });

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      const buildPayload = (scenario) => {
        const scenarioRates = matchedByKey[scenario.key]?.rates || rates;
        const payload = {
          width: Number(scenario.width) || 0,
          height: Number(scenario.height) || 0,
          length: Number(scenario.length) || 0,
          geometry_method: formValues.geometry_method,
          labor_basis: formValues.labor_basis,
          labor_rates: {
            cnc: Number(scenarioRates.cnc) || 0,
            cut: Number(scenarioRates.cut) || 0,
            fab: Number(scenarioRates.fab) || 0,
            paint: Number(scenarioRates.paint) || 0,
          },
          product_based_multiplier: 1,
        };
        if (materialId) payload.material_id = Number(materialId);
        if (finishRateId) payload.finish_rate_id = Number(finishRateId);
        if (
          formValues.min_labor_hours !== "" &&
          formValues.min_labor_hours !== null &&
          formValues.min_labor_hours !== undefined
        ) {
          payload.min_labor_hours = Number(formValues.min_labor_hours);
        }
        return payload;
      };

      Promise.allSettled(
        scenarios.map((s) =>
          previewRateCard(buildPayload(s), controller.signal),
        ),
      )
        .then((settled) => {
          if (controller.signal.aborted) return;
          const next = {};
          settled.forEach((res, idx) => {
            const key = scenarios[idx].key;
            if (res.status === "fulfilled") {
              next[key] = { data: res.value };
            } else {
              const reason = res.reason || {};
              if (reason.name === "CanceledError") return;
              next[key] = {
                error: reason.error || reason.message || "Preview failed",
              };
            }
          });
          setPreviews(next);
          // The shell's status strip reads the "Typical" scenario. It is the
          // endpoint's own `pricing` block, passed straight through — nothing
          // downstream recomputes any of it.
          if (onPricingRef.current) {
            onPricingRef.current(next.typical?.data?.pricing || null);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className="flex flex-col gap-4 p-3.5">
      {/* The pane header names this rail; here we only say what it does. */}
      <p className="text-xs text-[var(--wb-dim)]">
        Prices react as you edit the card.
      </p>

      {/* Shared material + finish selectors */}
      <div className="grid grid-cols-1 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium opacity-70">Material</span>
          <select
            value={materialId}
            onChange={(e) => setMaterialId(e.target.value)}
            className="h-9 rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] px-2 text-sm text-[var(--input-text-color)]"
          >
            <option value="">No material ($0/sqft)</option>
            {materials.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium opacity-70">Finish</span>
          <select
            value={finishRateId}
            onChange={(e) => setFinishRateId(e.target.value)}
            className="h-9 rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] px-2 text-sm text-[var(--input-text-color)]"
          >
            <option value="">No finish</option>
            {finishRates.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} ({formatMoney(f.cost_per_sqft)}/sqft)
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-col gap-3">
        {scenarios.map((scenario) => (
          <ScenarioCard
            key={scenario.key}
            scenario={scenario}
            preview={previews[scenario.key]}
            hasLabor={hasLabor}
            loading={loading}
            banded={banded}
            matched={matchedByKey[scenario.key]?.band}
            secondary={scenario.key !== "typical"}
            onDimChange={(dim, value) =>
              setScenarioDim(scenario.key, dim, value)
            }
          />
        ))}
      </div>
    </div>
  );
}

function ScenarioCard({
  scenario,
  preview,
  hasLabor,
  loading,
  banded,
  matched,
  secondary,
  onDimChange,
}) {
  const engine = preview?.data?.engine;
  const pricing = preview?.data?.pricing;
  const floorApplied = engine?.labor_floor_applied;
  const bandTag = banded
    ? matched
      ? `${matched.band_label || "band"} band`
      : "no band"
    : null;

  return (
    <div
      className={`rate-card-preview__scenario${
        secondary ? " rate-card-preview__scenario--secondary" : ""
      } rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-3 shadow-soft`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{scenario.label}</span>
        <span className="flex items-center gap-1">
          {bandTag && (
            <Badge
              variant={matched ? "outline" : "secondary"}
              className="text-[10px]"
            >
              {bandTag}
            </Badge>
          )}
          {floorApplied && (
            <Badge variant="secondary" className="text-[10px]">
              floor applied
            </Badge>
          )}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2">
        {["width", "height", "length"].map((dim) => (
          <label key={dim} className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase opacity-60">
              {dim === "length" ? "L" : dim === "width" ? "W" : "H"}
            </span>
            <Input
              type="number"
              min="0"
              step="0.25"
              value={scenario[dim]}
              onChange={(e) => onDimChange(dim, e.target.value)}
              onFocus={(e) => e.target.select()}
              className="h-8 px-2 text-right text-xs tabular-nums"
            />
          </label>
        ))}
      </div>

      {preview?.error ? (
        <p className="mt-2 text-xs text-[color:var(--error-color)]">
          {preview.error}
        </p>
      ) : (
        <div className="mt-3 space-y-1.5">
          <div className="flex justify-between text-xs opacity-70">
            <span>Square footage</span>
            <span className="tabular-nums">
              {engine ? `${formatNum(engine.calculated_sqft, 2)} sqft` : "—"}
            </span>
          </div>

          {hasLabor ? (
            <>
              <div className="text-xs opacity-70">
                {engine
                  ? `CNC ${formatNum(engine.cnc_hours_per_unit, 1)} · Cut ${formatNum(
                      engine.cut_hours_per_unit,
                      1,
                    )} · Fab ${formatNum(engine.fab_hours_per_unit, 1)} · Paint ${formatNum(
                      engine.paint_hours_per_unit,
                      1,
                    )} hr`
                  : "—"}
              </div>
              <div className="flex justify-between text-xs opacity-70">
                <span>Labor</span>
                <span className="tabular-nums">
                  {pricing ? formatMoney(pricing.labor_cost) : "—"}
                </span>
              </div>
              <div className="flex justify-between text-xs opacity-70">
                <span>Material + finish</span>
                <span className="tabular-nums">
                  {pricing ? formatMoney(pricing.material_cost) : "—"}
                </span>
              </div>
              <div className="mt-2 flex items-baseline justify-between border-t border-[var(--border-color)] pt-2">
                <span className="text-xs font-medium opacity-70">
                  Sell price
                </span>
                <span className="text-xl font-bold tabular-nums">
                  {pricing
                    ? formatMoney(pricing.sell_price)
                    : loading
                      ? "…"
                      : "—"}
                </span>
              </div>
            </>
          ) : (
            <div className="mt-2 rounded-md border border-dashed border-[var(--border-color)] px-2 py-3 text-center text-xs opacity-60">
              No labor priced yet — set department rates to see a sell price.
              {engine && (
                <div className="mt-1">
                  Material + finish: {formatMoney(pricing?.material_cost)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RateCardPreviewRail;
